/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018,2019. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Main } from '@govhhs/govhhs-design-system-react';
import {
  ScrollToTop,
  SessionTimeout,
  ErrorBoundary,
  SSOVerifier,
  Toaster,
  ConnectivityHandler,
  CookiesHandler,
} from '@spm/universal-access-ui';
// BEGIN - SD-1067 - RC - IEG address validation on UA Combo box
import { IEGRegistry } from '@spm/core';
import DOMPurify from 'dompurify';
import {
  searchMailingAddressCombo,
  searchResidentialAddressCombo,
} from './features/customFunctions/SDAddressValidation';
// END - SD-1067 - RC
import routes from './routes';
import ReduxInit from './redux/ReduxInit';
import IntlInit from './intl/IntlInit';
import CompatibilityInit from './compatibility/CompatibilityInit';

/* BEGIN SD-1039 YP - Replacing the ApplicationHeader and ApplicationHeaderComponent imports
 to use custom header in src/features folder */
import ApplicationHeader from './features/ApplicationHeader';
import ApplicationHeaderComponent from './features/ApplicationHeader/components/ApplicationHeaderComponent';
/* END SD-1039 YP - Replacing the ApplicationHeader and ApplicationHeaderComponent imports
 to use custom header in src/features folder */

/* BEGIN - SD-1049 YP - Replacing the OOTB ApplicationFooter import with custom ApplicationFooter import */
import ApplicationFooter from './features/ApplicationFooter';
/* END - SD-1049 YP - Replacing the OOTB ApplicationFooter import with custom ApplicationFooter import */

/**
 * App component entry point.
 */
const App = () => {
  DOMPurify.setConfig({ ADD_ATTR: ['target'] });
  // BEGIN - SD-1067 - RC - IEG address validation on UA Combo box
  IEGRegistry.registerComboBoxSearchFunctions({
    searchMailingAddressCombo,
    searchResidentialAddressCombo,
  });
  // END - SD-1067 - RC
  return (
    <ReduxInit>
      <IntlInit>
        <Router basename={process.env.PUBLIC_URL}>
          <ScrollToTop>
            <CompatibilityInit>
              <ErrorBoundary
                footer={<ApplicationFooter />}
                header={<ApplicationHeaderComponent hasErrorBeenCaught />}
                isHeaderBoundary
              >
                <CookiesHandler
                  footer={<ApplicationFooter />}
                  header={
                    <ApplicationHeaderComponent
                      isALinkedUser={() => false}
                      isAppealsEnabled={false}
                      isEmpty={false}
                    />
                  }
                >
                  <ConnectivityHandler>
                    <SSOVerifier
                      placeholder={
                        <>
                          <ApplicationHeaderComponent
                            isALinkedUser={() => false}
                            isAppealsEnabled={false}
                            isEmpty={false}
                          />
                          <Main
                            className="wds-u-bg--page wds-u-text-word-break"
                            id="maincontent"
                            pushFooter
                          >
                            {routes}
                          </Main>
                        </>
                      }
                    >
                      <ApplicationHeader />
                      <ErrorBoundary>
                        <SessionTimeout />
                        <Toaster />
                        <Main
                          className="wds-u-bg--page wds-u-text-word-break"
                          id="maincontent"
                          pushFooter
                        >
                          {routes}
                        </Main>
                      </ErrorBoundary>
                      <ApplicationFooter />
                    </SSOVerifier>
                  </ConnectivityHandler>
                </CookiesHandler>
              </ErrorBoundary>
            </CompatibilityInit>
          </ScrollToTop>
        </Router>
      </IntlInit>
    </ReduxInit>
  );
};

export default App;
